import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { 
  Star, Moon, Sun, Sparkle, ArrowRight, Heart, 
  Lightning, Crown, Check, X, Play, Users,
  BookOpen, Brain, Calendar, Shield, Infinity,
  Globe, CaretDown, Certificate, Scroll
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useLocation } from "../context/LocationContext";
import { VedicArchitectureCard } from "../components/VedicFooter";
import { getTiers } from "../config/pricing";

const API = process.env.REACT_APP_BACKEND_URL;

// ============== PRICING ==============
// Imported from /app/frontend/src/config/pricing.js (SSoT — mirrors backend)
// To update prices, edit ONLY /app/frontend/src/config/pricing.js
const PRICING_TIERS = getTiers();

// One-time focused reports (STANDARDIZED PRICING - Feb 2026)
// ALL reports = ₹2,499 / $69 (KULA tier gets all FREE)
const FOCUSED_REPORTS = [
  { id: "marriage", name: "Marriage Compatibility Deep Dive", priceINR: 2499, priceUSD: 69 },
  { id: "career", name: "Career Timing Report", priceINR: 2499, priceUSD: 69 },
  { id: "life_phase", name: "Life Phase Audit", priceINR: 2499, priceUSD: 69 }
];

// Legacy TIERS (free/premium/pro keys) for backward compatibility with older
// components in this file. Derived from canonical PRICING_TIERS so prices stay synced.
const _T = Object.fromEntries(PRICING_TIERS.map(t => [t.id, t]));
const TIERS = {
  free: {
    name: _T.prarambh.name,
    nameHindi: _T.prarambh.nameHindi,
    price: "Free",
    priceINR: _T.prarambh.monthlyPriceINR,
    priceUSD: _T.prarambh.monthlyPriceUSD,
    priceNum: _T.prarambh.monthlyPriceINR,
    yearlyPriceINR: _T.prarambh.yearlyPriceINR,
    yearlyPriceUSD: _T.prarambh.yearlyPriceUSD,
    period: "forever",
    description: "Start your journey with clarity",
    color: _T.prarambh.color,
    features: []
  },
  premium: {
    name: _T.marga.name,
    nameHindi: _T.marga.nameHindi,
    price: `₹${_T.marga.monthlyPriceINR}`,
    priceINR: _T.marga.monthlyPriceINR,
    priceUSD: _T.marga.monthlyPriceUSD,
    priceNum: _T.marga.monthlyPriceINR,
    yearlyPriceINR: _T.marga.yearlyPriceINR,
    yearlyPriceUSD: _T.marga.yearlyPriceUSD,
    period: "/month",
    description: _T.marga.tagline,
    color: "var(--truffle)",
    popular: true,
    features: []
  },
  pro: {
    name: _T.kula.name,
    nameHindi: _T.kula.nameHindi,
    price: `₹${_T.kula.monthlyPriceINR.toLocaleString("en-IN")}`,
    priceINR: _T.kula.monthlyPriceINR,
    priceUSD: _T.kula.monthlyPriceUSD,
    priceNum: _T.kula.monthlyPriceINR,
    yearlyPriceINR: _T.kula.yearlyPriceINR,
    yearlyPriceUSD: _T.kula.yearlyPriceUSD,
    period: "/month",
    description: _T.kula.tagline,
    color: "var(--burning-flame)",
    freeReports: true,
    features: []
  }
};


// Interactive Feature Cards with detailed hover information
const FEATURES = [
  {
    icon: Star,
    title: "Kundli Analysis",
    description: "Detailed birth chart with planetary positions, houses, and yogas",
    tier: "free",
    demo: "Interactive chart visualization",
    outcome: "Understand your cosmic blueprint",
    benefit: "Gain clarity on your life path, strengths, and challenges through ancient Vedic wisdom. Know your Ascendant, Moon sign, and planetary placements.",
    details: "Complete D1-D12 divisional charts • Planetary strengths (Shadbala) • Yoga detection • House analysis"
  },
  {
    icon: Brain,
    title: "AI Astrologer",
    description: "Ask any question and receive personalized Vedic guidance",
    tier: "premium",
    demo: "Chat with our AI sage",
    outcome: "Get instant, personalized answers",
    benefit: "No more waiting for appointments. Ask about career, relationships, health, or spirituality anytime and receive guidance based on YOUR birth chart.",
    details: "Conversational memory • Multi-turn dialogues • Context-aware responses • Transit-based insights"
  },
  {
    icon: Moon,
    title: "Daily Horoscope",
    description: "Personalized predictions based on your Moon sign and transits",
    tier: "free",
    demo: "Real-time cosmic insights",
    outcome: "Navigate each day with awareness",
    benefit: "Start every morning knowing which activities are favored, what to avoid, and how planetary transits specifically affect YOU today.",
    details: "Moon sign predictions • Transit effects • Lucky colors & numbers • Favorable timings"
  },
  {
    icon: Heart,
    title: "Compatibility",
    description: "Ashtakoot Guna matching with detailed relationship analysis",
    tier: "premium",
    demo: "36-point matching system",
    outcome: "Make informed relationship decisions",
    benefit: "Whether for marriage or partnership, understand the cosmic compatibility between two charts. Know strengths, challenges, and remedies.",
    details: "8 Kuta analysis • Manglik check • Nadi compatibility • Relationship remedies"
  },
  {
    icon: Calendar,
    title: "Muhurta Planner",
    description: "Find auspicious timings for important life events",
    tier: "pro",
    demo: "Smart event scheduling",
    outcome: "Launch ventures at the perfect time",
    benefit: "From business launches to weddings, ensure success by starting at cosmically aligned moments. Avoid Rahu Kalam and inauspicious periods.",
    details: "Event-specific muhurtas • Choghadiya calculator • Panchang integration • Custom date analysis"
  },
  {
    icon: BookOpen,
    title: "Kids Learning",
    description: "Nakshatra stories with audio in Hindi, English & Sanskrit",
    tier: "pro",
    demo: "27 interactive stories",
    outcome: "Instill Vedic wisdom in children",
    benefit: "Make astrology fun and accessible for the next generation through engaging animated stories about each Nakshatra's deity and mythology.",
    details: "27 Nakshatra tales • Trilingual audio • Interactive quizzes • Progress tracking"
  },
  {
    icon: Users,
    title: "Family Sync",
    description: "Track and manage your family's cosmic journey together",
    tier: "premium",
    demo: "Shared spiritual goals",
    outcome: "Align your family's spiritual practice",
    benefit: "One dashboard for the whole family. Track everyone's birth charts, set shared ritual reminders, and understand family dynamics astrologically.",
    details: "Multi-profile management • Shared calendars • Family compatibility • Joint muhurta planning"
  },
  {
    icon: Lightning,
    title: "Remedies",
    description: "Personalized gemstones, mantras, and ritual recommendations",
    tier: "pro",
    demo: "Customized solutions",
    outcome: "Balance planetary influences",
    benefit: "Receive specific remedies for YOUR chart's weaknesses. Know which gemstone to wear, which mantra to chant, and which deity to worship.",
    details: "Gemstone recommendations • Personalized mantras • Ritual guides • Donation suggestions"
  }
];

// Logo Components - Using centralized Logo component
import { Logo, LogoIcon } from "../components/Logo";

// Mandala Pattern
const MandalaPattern = ({ className }) => (
  <div className={`mandala-bg ${className}`} />
);

// Pricing Card Component - Horizontal Bar Layout with Outcomes
const PricingBar = ({ tier, data, onSelect, formatPrice, isIndia, isLast }) => {
  // Display price based on location
  const getDisplayPrice = () => {
    if (tier === 'free') return 'Free';
    if (isIndia) return `₹${data.priceINR}`;
    return `$${data.priceUSD}`;
  };
  
  const displayPrice = getDisplayPrice();
  const periodText = tier === 'free' ? 'forever' : data.period;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ 
        duration: 0.7, 
        delay: tier === 'free' ? 0.1 : tier === 'premium' ? 0.2 : 0.3,
        ease: [0.34, 1.56, 0.64, 1]
      }}
      whileHover={{ 
        y: -4,
        transition: { duration: 0.3 }
      }}
      className={`relative ${!isLast ? 'mb-6' : ''} ${data.popular ? 'pricing-popular' : 'pricing-card-animated'}`}
      data-testid={`pricing-card-${tier}`}
    >
      {/* Popular Badge with animation */}
      {data.popular && (
        <motion.div 
          className="absolute -top-3 left-6 z-10"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
        >
          <span className="bg-gradient-to-r from-[var(--truffle)] to-[var(--truffle-dark)] text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 tier-badge-animated">
            <Star size={12} weight="fill" />
            MOST CHOSEN
          </span>
        </motion.div>
      )}
      
      <div className={`bg-white rounded-2xl border-2 ${data.popular ? 'border-[var(--truffle)] shadow-lg shadow-[var(--truffle)]/10' : 'border-[var(--border-gold)]'} overflow-hidden transition-shadow duration-500 hover:shadow-xl`}>
        {/* Header Bar */}
        <div className={`p-4 sm:p-6 ${data.popular ? 'bg-gradient-to-r from-[var(--palladian-light)] to-[var(--bg-surface)]' : 'bg-[var(--bg-subtle)]'}`}>
          <div className="flex flex-col gap-4">
            {/* Top Row: Plan Name & Icon */}
            <div className="flex items-center gap-3 sm:gap-4">
              <motion.div 
                className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  tier === 'pro' ? 'bg-gradient-to-br from-[var(--burning-flame)] to-[var(--truffle)]' :
                  tier === 'premium' ? 'bg-gradient-to-br from-[var(--truffle)] to-[var(--truffle-dark)]' :
                  'bg-gradient-to-br from-[var(--palladian)] to-[var(--palladian-dark)]'
                }`}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {tier === 'pro' && <Crown size={24} weight="fill" className="text-white sm:w-7 sm:h-7" />}
                {tier === 'premium' && <Star size={24} weight="fill" className="text-white sm:w-7 sm:h-7" />}
                {tier === 'free' && <Sparkle size={24} weight="fill" className="text-white sm:w-7 sm:h-7" />}
              </motion.div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base sm:text-xl font-cinzel font-semibold text-[var(--text-primary)]">{data.name}</h3>
                  {data.nameHindi && (
                    <span className="text-xs sm:text-sm text-[var(--text-muted)] font-manrope">({data.nameHindi})</span>
                  )}
                </div>
                <p className="text-xs sm:text-sm font-manrope text-[var(--text-secondary)] line-clamp-1">{data.description}</p>
              </div>
            </div>
            
            {/* Bottom Row: Price & CTA */}
            <div className="flex items-center justify-between gap-4">
              <motion.div 
                className="flex flex-col"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl sm:text-4xl font-mono font-bold text-[var(--text-primary)] price-animated">{displayPrice}</span>
                  {periodText && <span className="text-sm sm:text-lg font-manrope text-[var(--text-muted)]">{periodText}</span>}
                </div>
                {tier !== 'free' && !isIndia && (
                  <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">or ₹{data.priceINR}/month in India</span>
                )}
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  className={`min-w-[120px] sm:min-w-[160px] text-sm sm:text-base ${data.popular ? 'cta-animated' : ''} ${
                    tier === 'pro' ? 'btn-gold' : 
                    tier === 'premium' ? 'bg-gradient-to-r from-[var(--truffle)] to-[var(--truffle-dark)] hover:from-[var(--truffle-dark)] hover:to-[var(--truffle-dark)] text-white' : 
                    'btn-outline'
                  }`}
                  onClick={() => onSelect(tier)}
                  data-testid={`select-${tier}-btn`}
                >
                  {tier === 'free' ? 'Start Free' : 'Get Started'}
                  <ArrowRight size={16} className="ml-1 sm:ml-2 sm:w-[18px] sm:h-[18px]" />
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Features with Outcomes - animated list */}
        <div className="p-4 sm:p-6 border-t border-[var(--border-gold)]/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 feature-list-animated">
            {data.features.map((feature, idx) => (
              <motion.div 
                key={`${feature}-${idx}`}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + idx * 0.05, duration: 0.3 }}
                className={`flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl transition-all duration-300 hover:scale-[1.02] ${feature.highlight ? 'bg-[var(--gold)]/10' : 'bg-[var(--bg-subtle)]/50'}`}
              >
                <Check size={16} className={`flex-shrink-0 mt-0.5 ${feature.highlight ? 'text-[var(--gold)]' : 'text-[var(--palladian-dark)]'}`} />
                <div>
                  <p className={`text-xs sm:text-sm font-manrope font-medium ${feature.highlight ? 'text-[var(--gold)]' : 'text-[var(--text-primary)]'}`}>
                    {feature.name}
                  </p>
                  <p className="text-[10px] sm:text-xs font-manrope text-[var(--text-muted)] mt-0.5 leading-relaxed">
                    {feature.outcome}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Detailed Feature Comparison Table Component
const FeatureComparisonTable = () => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b-2 border-[var(--border-gold)]">
            <th className="text-left py-4 px-4 font-cinzel text-[var(--text-primary)]">Features</th>
            <th className="text-center py-4 px-4 font-cinzel text-[var(--text-primary)]">
              <div className="flex flex-col items-center">
                <Sparkle size={20} className="text-[var(--primary)] mb-1" />
                <span>PRARAMBH</span>
                <span className="text-xs font-manrope text-[var(--text-muted)] font-normal">Free</span>
              </div>
            </th>
            <th className="text-center py-4 px-4 font-cinzel text-[var(--text-primary)] bg-[var(--truffle)]/5">
              <div className="flex flex-col items-center">
                <Star size={20} className="text-[var(--truffle)] mb-1" />
                <span>MARGA</span>
                <span className="text-xs font-manrope text-[var(--text-muted)] font-normal">₹699/mo</span>
              </div>
            </th>
            <th className="text-center py-4 px-4 font-cinzel text-[var(--text-primary)]">
              <div className="flex flex-col items-center">
                <Crown size={20} className="text-[var(--gold)] mb-1" />
                <span>KULA</span>
                <span className="text-xs font-manrope text-[var(--text-muted)] font-normal">₹1,499/mo</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {FEATURE_COMPARISON.map((category, catIdx) => (
            <>
              <tr key={`cat-${catIdx}`} className="bg-[var(--bg-subtle)]">
                <td colSpan={4} className="py-3 px-4 font-cinzel font-semibold text-[var(--text-primary)] text-sm">
                  {category.category}
                </td>
              </tr>
              {category.features.map((feature, featIdx) => (
                <tr key={`feat-${catIdx}-${featIdx}`} className="border-b border-[var(--border-gold)]/20 hover:bg-[var(--bg-subtle)]/50 transition-colors">
                  <td className="py-3 px-4 font-manrope text-sm text-[var(--text-secondary)]">
                    {feature.name}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {feature.free === true ? (
                      <Check size={20} className="text-[var(--palladian-dark)] mx-auto" />
                    ) : feature.free === false ? (
                      <span className="text-[var(--text-muted)]">—</span>
                    ) : (
                      <span className="text-xs font-manrope text-[var(--text-secondary)] bg-[var(--bg-subtle)] px-2 py-1 rounded">{feature.free}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center bg-[var(--truffle)]/5">
                    {feature.premium === true ? (
                      <Check size={20} className="text-[var(--status-success)] mx-auto" />
                    ) : feature.premium === false ? (
                      <span className="text-[var(--text-muted)]">—</span>
                    ) : (
                      <span className="text-xs font-manrope text-[var(--truffle)] bg-[var(--truffle)]/10 px-2 py-1 rounded font-medium">{feature.premium}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {feature.pro === true ? (
                      <Check size={20} className="text-[var(--burning-flame)] mx-auto" />
                    ) : feature.pro === false ? (
                      <span className="text-[var(--text-muted)]">—</span>
                    ) : (
                      <span className="text-xs font-manrope text-[var(--burning-flame)] bg-[var(--burning-flame)]/10 px-2 py-1 rounded font-medium">{feature.pro}</span>
                    )}
                  </td>
                </tr>
              ))}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Feature Card Component with premium animations
const FeatureCard = ({ feature, index, isHero = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const tierColors = {
    free: 'bg-[#F5F0E6] border-[#E6D5A6]',
    premium: 'bg-[#E3F2FD] border-[#90CAF9]',
    pro: 'bg-[#FFF8E1] border-[#D4AF37]'
  };

  const tierGradients = {
    free: 'from-[#E6D5A6]/20 to-[#F5F0E6]',
    premium: 'from-[#90CAF9]/20 to-[#E3F2FD]',
    pro: 'from-[#D4AF37]/20 to-[#FFF8E1]'
  };

  // Handle both hover (desktop) and tap (mobile)
  const handleInteraction = () => {
    setIsExpanded(!isExpanded);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ 
        delay: index * 0.1,
        duration: 0.6,
        ease: [0.34, 1.56, 0.64, 1]
      }}
      whileHover={{ 
        y: -8, 
        scale: 1.02,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      className="relative group card-glow-animated"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      onClick={handleInteraction}
      data-testid={`feature-card-${index}`}
    >
      {/* Main Card */}
      <div className={`card-interactive ${isHero ? 'p-4 sm:p-8' : 'p-4 sm:p-6'} cursor-pointer transition-all duration-500 ${isExpanded ? 'shadow-xl' : ''} h-full`}>
        {/* Icon with float animation */}
        <motion.div 
          className={`${isHero ? 'w-12 h-12 sm:w-16 sm:h-16' : 'w-10 h-10 sm:w-14 sm:h-14'} rounded-xl flex items-center justify-center mb-3 sm:mb-4 transition-all duration-500 ${tierColors[feature.tier]} border card-icon-animated`}
          whileHover={{ 
            rotate: [0, -5, 5, 0],
            scale: 1.1,
            transition: { duration: 0.5 }
          }}
        >
          <feature.icon size={isHero ? 24 : 20} className="text-[var(--primary)] sm:w-7 sm:h-7" weight="duotone" />
        </motion.div>
        
        {/* Content with staggered animation */}
        <div className="card-content-animated">
          <h3 className={`font-cinzel ${isHero ? 'text-base sm:text-xl' : 'text-sm sm:text-lg'} text-[#1B2632] mb-2 sm:mb-3`}>{feature.title}</h3>
          
          {/* Description */}
          <p className={`${isHero ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'} text-[#2C3B4D] mb-2 sm:mb-3 leading-relaxed ${isHero ? '' : 'line-clamp-2'}`}>
            {feature.description}
          </p>
          
          {/* Outcome Preview with subtle animation */}
          <motion.div 
            className="mb-3 sm:mb-4 p-2 rounded-lg bg-[var(--bg-subtle)] border-l-2 border-[var(--gold)]"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-[10px] sm:text-xs text-[var(--gold)] font-medium">✨ {feature.outcome}</p>
          </motion.div>
          
          {/* Benefit (shown on hero cards) */}
          {isHero && (
            <p className="text-xs sm:text-sm text-[#2C3B4D] mb-3 sm:mb-4 leading-relaxed line-clamp-3 sm:line-clamp-none">
              {feature.benefit}
            </p>
          )}
          
          {/* Tier & Demo */}
          <div className="flex items-center justify-between">
            <span className={`tier-badge tier-${feature.tier} tier-badge-animated text-[10px] sm:text-xs`}>
              {feature.tier === 'free' ? '🌟 Free' : feature.tier === 'premium' ? '⭐ Premium' : '👑 Pro'}
            </span>
            <span className="text-[10px] sm:text-xs text-[var(--gold)] font-mono opacity-70 hidden sm:inline">{feature.demo}</span>
          </div>
        </div>
        
        {/* Hover indicator */}
        {!isHero && (
          <motion.div 
            className="absolute bottom-2 right-2 hidden sm:block"
            initial={{ opacity: 0.5 }}
            animate={{ opacity: isExpanded ? 0 : 0.5 }}
            transition={{ duration: 0.2 }}
          >
            <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">Tap for details</span>
          </motion.div>
        )}
      </div>
      
      {/* Expanded Detail Panel */}
      {!isHero && (
        <motion.div
          initial={false}
          animate={{
            opacity: isExpanded ? 1 : 0,
            y: isExpanded ? 0 : 10,
            scale: isExpanded ? 1 : 0.95,
            pointerEvents: isExpanded ? 'auto' : 'none'
          }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className={`absolute top-full left-0 right-0 mt-2 z-50 rounded-xl shadow-2xl border overflow-hidden bg-gradient-to-br ${tierGradients[feature.tier]}`}
          style={{ 
            backgroundColor: 'var(--bg-paper)',
            borderColor: feature.tier === 'pro' ? '#D4AF37' : feature.tier === 'premium' ? '#90CAF9' : '#E6D5A6',
            maxHeight: '280px'
          }}
        >
          <div className="p-3 sm:p-5">
            {/* Outcome */}
            <div className="mb-2 sm:mb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[var(--success)]/20 flex items-center justify-center">
                  <Check size={10} className="text-[var(--success)] sm:w-3 sm:h-3" weight="bold" />
                </div>
                <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-[var(--success)]">Outcome</span>
              </div>
              <p className="text-xs sm:text-sm font-medium text-[var(--text-primary)]">{feature.outcome}</p>
            </div>
            
            {/* Benefit */}
            <div className="mb-2 sm:mb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[var(--gold)]/20 flex items-center justify-center">
                  <Sparkle size={10} className="text-[var(--gold)] sm:w-3 sm:h-3" weight="fill" />
                </div>
                <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-[var(--gold)]">Benefit</span>
              </div>
              <p className="text-[10px] sm:text-xs text-[var(--text-secondary)] leading-relaxed line-clamp-3">{feature.benefit}</p>
            </div>
            
            {/* Details */}
            <div className="pt-2 border-t border-[var(--border)]">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                  <Infinity size={10} className="text-[var(--primary)] sm:w-3 sm:h-3" />
                </div>
                <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-[var(--primary)]">Includes</span>
              </div>
              <p className="text-[9px] sm:text-[10px] text-[var(--text-muted)] font-mono line-clamp-2">{feature.details}</p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showDetailedComparison, setShowDetailedComparison] = useState(false);
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'
  const { country, setCountry, countries, getCountryName, currencySymbol, formatPrice, isIndia } = useLocation();

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    navigate("/auth", { state: { selectedTier: tier } });
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Helmet>
        <title>AADHYAA - Vedic Astrology Intelligence Platform</title>
        <meta name="description" content="Discover deep self-awareness through Vedic astrology. Personalized insights for career, health, wealth, relationships powered by classical Jyotish wisdom." />
        <meta property="og:title" content="AADHYAA - Vedic Astrology Intelligence Platform" />
        <meta property="og:description" content="Personalized Vedic astrology insights powered by classical Jyotish wisdom." />
      </Helmet>
      <header className="fixed top-0 left-0 right-0 z-50 py-5 px-6 lg:px-12 bg-gradient-to-b from-[var(--bg-cream)] via-[var(--bg-cream)]/95 to-transparent">
        <div className="flex items-center justify-end max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            {/* Insights Button - Matching aadhyaa.in style */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              onClick={() => navigate("/insights")}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 border border-[var(--truffle)]/20 text-[var(--truffle)] hover:bg-[var(--truffle)]/5 hover:border-[var(--truffle)]/40 transition-all shadow-sm"
              data-testid="header-insights-btn"
            >
              <Sparkle size={16} weight="fill" className="text-[var(--burning-flame)]" />
              <span className="text-sm font-medium">Insights</span>
            </motion.button>
            
            {/* Country Selector */}
            <div className="relative">
              <button
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/80 border border-[var(--gold)]/30 hover:border-[var(--gold)] transition-colors text-sm"
                data-testid="country-selector"
              >
                <Globe className="w-4 h-4 text-[var(--gold)]" />
                <span className="text-[var(--text-dark)] font-medium">{getCountryName(country)}</span>
                <CaretDown className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showCountryDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowCountryDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-64 max-h-80 overflow-y-auto bg-white rounded-xl shadow-xl border border-[var(--gold)]/20 z-50">
                    <div className="sticky top-0 bg-white p-3 border-b border-[var(--gold)]/10">
                      <p className="text-xs text-[var(--text-muted)]">Select your location</p>
                    </div>
                    {countries.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => {
                          setCountry(c.code);
                          setShowCountryDropdown(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-[var(--gold)]/10 transition-colors flex items-center justify-between ${
                          country === c.code ? 'bg-[var(--gold)]/10 text-[var(--gold)]' : 'text-[var(--text-dark)]'
                        }`}
                        data-testid={`country-option-${c.code}`}
                      >
                        <span>{c.name}</span>
                        {country === c.code && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Button 
                className="btn-primary"
                onClick={() => navigate("/auth")}
                data-testid="header-login-btn"
              >
                Sign In
              </Button>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-background min-h-screen flex items-center relative overflow-hidden pt-16 sm:pt-20">
        {/* Floating Mandalas */}
        <MandalaPattern className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 hidden sm:block" />
        <MandalaPattern className="absolute top-1/3 right-0 translate-x-1/4 hidden sm:block" />
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-20 w-4 h-4 rounded-full bg-[var(--gold)] animate-float opacity-60 hidden sm:block" />
        <div className="absolute bottom-40 left-20 w-3 h-3 rounded-full bg-[var(--primary)] animate-float opacity-40 hidden sm:block" style={{animationDelay: '1s'}} />
        <div className="absolute top-1/3 right-1/3 w-2 h-2 rounded-full bg-[var(--saffron)] animate-float opacity-50 hidden sm:block" style={{animationDelay: '2s'}} />
        
        <div className="container-app relative z-10 py-8 sm:py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            {/* Left Content - Takes 7 columns on large screens */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 flex flex-col justify-center"
            >
              {/* What is AADHYAA - Clear descriptor */}
              <div className="mb-4 sm:mb-5">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--truffle)]/10 border border-[var(--truffle)]/20 text-[var(--truffle)] text-xs sm:text-sm font-manrope font-medium">
                  <Sparkle size={14} weight="fill" className="text-[var(--gold)]" />
                  Your Personal Vedic Decision Intelligence App
                </span>
              </div>
              
              {/* Hero Statement - Logo + Headline */}
              <div className="mb-6 sm:mb-8">
                <Logo variant="full" size="2xl" className="mb-4" testId="hero-logo" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-cinzel font-medium text-[var(--primary)] mt-4 sm:mt-6 leading-tight">
                  Know when to act.<br />Know when to wait.
                </h1>
              </div>
              
              {/* Sub-hero Statement */}
              <div className="max-w-xl mb-8 sm:mb-10">
                <p className="text-base sm:text-lg lg:text-xl font-manrope text-[var(--text-secondary)] leading-relaxed mb-4">
                  Life's biggest decisions — career, relationships, health, family — deserve more than guesswork.
                </p>
                <p className="text-base sm:text-lg lg:text-xl font-manrope text-[var(--text-secondary)] leading-relaxed">
                  AADHYAA combines Vedic timing wisdom, multi-system astrology, and responsible AI to help you understand when to act, pause, or prepare.
                </p>
              </div>
              
              {/* Engine Reliability Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mb-8 sm:mb-10"
              >
                <div className="inline-flex items-start gap-4 p-4 sm:p-5 bg-white/90 backdrop-blur-sm rounded-xl border border-[var(--palladian)]/40 shadow-lg max-w-lg">
                  <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[var(--palladian)] to-[var(--palladian-dark)] flex items-center justify-center">
                    <Shield size={22} className="text-white sm:w-6 sm:h-6" weight="fill" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm sm:text-base font-cinzel font-semibold text-[var(--text-primary)]">Engine Reliability</span>
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-[var(--palladian)]/20 text-[var(--palladian-dark)] rounded-full">Verified</span>
                    </div>
                    <p className="text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed">
                      Our core engine has been batch-tested across <span className="font-semibold text-[var(--truffle)]">100,000+ charts</span>, delivering <span className="font-semibold text-[var(--palladian-dark)]">99.9% computational consistency</span> across all core calculations.
                    </p>
                  </div>
                </div>
              </motion.div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button 
                  className="btn-primary text-base sm:text-lg px-8 sm:px-10 py-4 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1"
                  onClick={() => navigate("/auth")}
                  data-testid="hero-cta-btn"
                >
                  Start Free
                  <ArrowRight size={20} className="ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="text-base px-6 py-4 border-2 border-[var(--truffle)]/30 text-[var(--truffle)] hover:bg-[var(--truffle)]/5 transition-all"
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-secondary-cta"
                >
                  View Plans
                </Button>
              </div>
            </motion.div>
            
            {/* Right - Cosmic Visual + Live Snapshot Card - Takes 5 columns */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="hidden lg:flex lg:col-span-5 relative items-center justify-center"
            >
              <div className="relative w-full max-w-md mx-auto">
                {/* D-022 FIX: Above-the-fold "Today's Insight" sample card —
                    balances the right column whitespace at desktop widths and
                    gives visitors a concrete preview of the value before scroll. */}
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.4 }}
                  className="relative z-20 mx-auto mb-6 bg-white rounded-2xl border-2 border-[var(--burning-flame)]/40 shadow-[0_24px_60px_-12px_rgba(160,80,40,0.18)] p-5 sm:p-6 max-w-sm"
                  data-testid="hero-snapshot-card"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--truffle)]">
                      Today's Snapshot · Live
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--palladian-dark)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--palladian)] animate-pulse" />
                      now
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-cinzel font-semibold text-[var(--text-primary)]">72</span>
                    <span className="text-xs text-[var(--text-secondary)]">/ 100 alignment</span>
                  </div>
                  <p className="text-sm font-manrope text-[var(--text-secondary)] leading-relaxed mb-3">
                    Moon in <span className="font-semibold text-[var(--truffle)]">Rohini</span> supports
                    creative work and family conversations. Avoid signing
                    long-term contracts after 4 PM IST.
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-[var(--border-muted)]">
                    <span className="text-[10px] text-[var(--text-muted)]">
                      Sample · personalized when you join
                    </span>
                    <ArrowRight size={12} className="text-[var(--truffle)]" />
                  </div>
                </motion.div>

                <div className="relative w-full aspect-square max-w-md mx-auto">
                  {/* Outer Ring */}
                  <div className="absolute inset-0 rounded-full border-2 border-[var(--gold)]/20 animate-rotate-slow" style={{animationDuration: '60s'}} />
                  
                  {/* Second Ring */}
                  <div className="absolute inset-6 rounded-full border border-[var(--palladian)]/30 animate-rotate-slow" style={{animationDuration: '45s', animationDirection: 'reverse'}} />
                  
                  {/* Middle Ring */}
                  <div className="absolute inset-12 rounded-full border border-[var(--primary)]/20" />
                  
                  {/* Inner Glow */}
                  <div className="absolute inset-20 rounded-full bg-gradient-to-br from-[var(--gold)]/20 to-[var(--primary)]/10 animate-pulse-glow" />
                  
                  {/* Center Element */}
                  <div className="absolute inset-28 rounded-full bg-gradient-to-br from-[var(--gold)] to-[#AA8A28] shadow-xl">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-tl from-transparent to-white/20" />
                  </div>
                
                {/* Orbital Elements */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-[var(--saffron)] to-orange-600 shadow-lg flex items-center justify-center animate-float">
                  <Sun size={18} className="text-white" weight="fill" />
                </div>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 shadow-lg flex items-center justify-center animate-float" style={{animationDelay: '1s'}}>
                  <Moon size={14} className="text-white" weight="fill" />
                </div>
                <div className="absolute top-1/2 left-2 -translate-y-1/2 w-5 h-5 rounded-full bg-gradient-to-br from-[var(--burning-flame)] to-[var(--burning-flame-dark)] shadow-lg animate-float" style={{animationDelay: '0.5s'}} />
                <div className="absolute top-1/2 right-2 -translate-y-1/2 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg animate-float" style={{animationDelay: '1.5s'}} />
                
                {/* Decorative Stars */}
                <div className="absolute top-1/4 right-1/4 w-2 h-2 rounded-full bg-white shadow-sm animate-twinkle" />
                <div className="absolute bottom-1/4 left-1/4 w-1.5 h-1.5 rounded-full bg-white shadow-sm animate-twinkle" style={{animationDelay: '0.5s'}} />
                <div className="absolute top-1/3 left-1/5 w-1 h-1 rounded-full bg-white shadow-sm animate-twinkle" style={{animationDelay: '1s'}} />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Decorative Diyas */}
        <div className="absolute bottom-10 left-10 text-4xl animate-diya hidden sm:block">🪔</div>
        <div className="absolute bottom-20 right-16 text-3xl animate-diya hidden sm:block" style={{animationDelay: '1s'}}>🪔</div>
      </section>

      {/* Section 2: Your Complete Life Dashboard */}
      <section className="py-16 sm:py-28 bg-gradient-to-b from-[var(--bg-cream)] via-white to-[var(--bg-paper)] relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[var(--palladian)]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[var(--burning-flame)]/5 rounded-full blur-3xl" />
        
        <div className="container-app relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-20"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[var(--palladian)]/20 to-[var(--burning-flame)]/20 text-[var(--truffle)] text-sm font-manrope font-semibold mb-4 border border-[var(--palladian)]/30">
              <Sparkle size={14} weight="fill" className="text-[var(--burning-flame)]" />
              Your Complete Life Dashboard
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-cinzel mb-4 sm:mb-6 text-[var(--text-primary)] leading-tight">
              Seven Dimensions of<br className="hidden sm:block" /> <span className="text-[var(--burning-flame)]">Conscious Living</span>
            </h2>
            <p className="text-lg sm:text-xl text-[var(--text-secondary)] max-w-3xl mx-auto leading-relaxed">
              AADHYAA transforms ancient Vedic wisdom into a living practice — 
              organized into seven essential areas that guide your everyday decisions.
            </p>
          </motion.div>

          {/* 7 Section Showcase Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
            
            {/* Card 1: My Snapshot */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[var(--palladian)]/20 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[var(--palladian)] to-[var(--palladian-dark)] flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform">
                  <Sun size={28} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">My Snapshot</h3>
                <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed">
                  Wake up knowing exactly where you are. Your life phase, energy rhythm, focus areas, and today's guidance — all in one calm overview.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs bg-[var(--palladian)]/10 text-[var(--truffle)] rounded-full">Life Phase</span>
                  <span className="px-3 py-1 text-xs bg-[var(--palladian)]/10 text-[var(--truffle)] rounded-full">Focus Areas</span>
                  <span className="px-3 py-1 text-xs bg-[var(--palladian)]/10 text-[var(--truffle)] rounded-full">Energy Rhythm</span>
                </div>
              </div>
            </motion.div>

            {/* Card 2: My Life */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[var(--burning-flame)]/15 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[var(--burning-flame)] to-[var(--truffle)] flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform">
                  <Brain size={28} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">My Life</h3>
                <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed">
                  Understand who you truly are. Your strengths, sensitivities, and long-term themes — across career, health, wealth, and personal growth.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs bg-[var(--burning-flame)]/10 text-[var(--burning-flame)] rounded-full">Career</span>
                  <span className="px-3 py-1 text-xs bg-[var(--burning-flame)]/10 text-[var(--burning-flame)] rounded-full">Health</span>
                  <span className="px-3 py-1 text-xs bg-[var(--burning-flame)]/10 text-[var(--burning-flame)] rounded-full">Wealth</span>
                  <span className="px-3 py-1 text-xs bg-[var(--burning-flame)]/10 text-[var(--burning-flame)] rounded-full">Growth</span>
                </div>
              </div>
            </motion.div>

            {/* Card 3: Timing & Decisions */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-[var(--gold)] shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute -top-3 left-6 px-3 py-1 bg-gradient-to-r from-[var(--gold)] to-amber-500 text-white text-xs font-bold rounded-full shadow-md">
                MOST VALUED
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[var(--gold)]/20 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[var(--gold)] to-amber-600 flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform">
                  <Lightning size={28} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">Timing & Decisions</h3>
                <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed">
                  The question that matters most: "When?" Get clarity on when to act, pause, or prepare — for career, marriage, health, and more.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs bg-[var(--gold)]/15 text-amber-700 rounded-full font-medium">Action Windows</span>
                  <span className="px-3 py-1 text-xs bg-[var(--gold)]/15 text-amber-700 rounded-full font-medium">Readiness Scores</span>
                </div>
              </div>
            </motion.div>

            {/* Card 4: Relationships & Family */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 card-glow-animated"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[var(--burning-flame)]/20 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[var(--burning-flame)] to-[var(--burning-flame-dark)] flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform card-icon-animated">
                  <Heart size={28} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">Relationships & Family</h3>
                <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed font-manrope">
                  Navigate connections with understanding, not anxiety. Partner compatibility, family harmony, and children's guidance — calmly.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs bg-[var(--burning-flame)]/10 text-[var(--burning-flame)] rounded-full font-manrope">Compatibility</span>
                  <span className="px-3 py-1 text-xs bg-[var(--burning-flame)]/10 text-[var(--burning-flame)] rounded-full font-manrope">Family Sync</span>
                  <span className="px-3 py-1 text-xs bg-[var(--burning-flame)]/10 text-[var(--burning-flame)] rounded-full font-manrope">Children</span>
                </div>
              </div>
            </motion.div>

            {/* Card 5: Ask AADHYAA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 card-glow-animated"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[var(--truffle)]/15 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[var(--truffle)] to-[var(--truffle-dark)] flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform card-icon-animated">
                  <Brain size={28} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">Ask AADHYAA</h3>
                <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed font-manrope">
                  Your personal Vedic advisor. Ask any question and receive thoughtful answers grounded in YOUR life data — not generic predictions.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs bg-[var(--truffle)]/10 text-[var(--truffle)] rounded-full font-manrope">Contextual AI</span>
                  <span className="px-3 py-1 text-xs bg-[var(--truffle)]/10 text-[var(--truffle)] rounded-full font-manrope">Your Chart</span>
                </div>
              </div>
            </motion.div>

            {/* Card 6: Learn & Grow */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 card-glow-animated"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[var(--palladian)]/20 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[var(--palladian)] to-[var(--palladian-dark)] flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform card-icon-animated">
                  <BookOpen size={28} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">Learn & Grow</h3>
                <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed font-manrope">
                  Wisdom without dogma. Explainers, stories, daily practices, and a dedicated Kids Gurukul — building values across generations.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs bg-[var(--palladian)]/15 text-[var(--palladian-dark)] rounded-full font-manrope">Stories</span>
                  <span className="px-3 py-1 text-xs bg-[var(--palladian)]/15 text-[var(--palladian-dark)] rounded-full font-manrope">Practices</span>
                  <span className="px-3 py-1 text-xs bg-[var(--palladian)]/15 text-[var(--palladian-dark)] rounded-full font-manrope">Kids Gurukul</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Reports Highlight Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[var(--truffle)] via-[var(--truffle-dark)] to-[var(--truffle)] rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-white relative overflow-hidden mb-12"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full mb-4">
                  <Scroll size={14} className="text-[var(--gold)]" />
                  <span className="text-xs font-medium text-[var(--gold)]">7th Section: Reports</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-cinzel font-bold mb-3">Detailed Reports On Demand</h3>
                <p className="text-base sm:text-lg text-white/80 max-w-2xl">
                  Go deeper when you need it. Life reports, decision guides, career insights, and relationship analysis — downloadable, shareable, and beautifully formatted.
                </p>
              </div>
              <div className="flex gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Users size={24} className="text-white/80" />
                </div>
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Lightning size={24} className="text-white/80" />
                </div>
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Heart size={24} className="text-white/80" />
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Section CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button 
              className="btn-primary text-lg px-10 py-5 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1"
              onClick={() => navigate("/auth")}
              data-testid="section2-cta-btn"
            >
              Start Your Journey Free
              <ArrowRight size={20} className="ml-2" />
            </Button>
            <p className="text-sm text-[var(--text-muted)] mt-4">No credit card required. Experience all 7 sections instantly.</p>
          </motion.div>
        </div>
      </section>
      
      {/* Section 3: How AADHYAA Works - Transparent Trust */}
      <section className="py-16 sm:py-28 bg-gradient-to-b from-[var(--bg-cream)] to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, var(--gold) 1px, transparent 0)', backgroundSize: '40px 40px'}} />
        </div>
        
        <div className="container-app relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--palladian-light)] text-[var(--truffle)] text-sm font-manrope font-semibold mb-4 border border-[var(--palladian)]">
              <Certificate size={14} weight="fill" className="text-[var(--palladian-dark)]" />
              How It Actually Works
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-cinzel mb-4 sm:mb-6 text-[var(--text-primary)] leading-tight">
              Not Predictions. <span className="text-[var(--truffle)]">Practice.</span>
            </h2>
            <p className="text-lg sm:text-xl text-[var(--text-secondary)] max-w-3xl mx-auto leading-relaxed font-manrope">
              AADHYAA doesn't tell you what will happen. It helps you understand your rhythm 
              so you can make better choices — calmly, clearly, consistently.
            </p>
          </motion.div>
          
          {/* The Process Flow */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg h-full card-glow-animated">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-br from-[var(--gold)] to-[var(--burning-flame)] text-white flex items-center justify-center font-cinzel font-bold text-xl shadow-lg">1</div>
                <div className="pt-4">
                  <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">Your Birth Data</h3>
                  <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed font-manrope">
                    Date, time, and place of birth — that's all we need. This becomes your permanent cosmic signature.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[var(--truffle)] font-medium font-manrope">
                    <Shield size={14} className="text-[var(--palladian-dark)]" weight="fill" />
                    <span>Encrypted & private. Never shared.</span>
                  </div>
                </div>
              </div>
              {/* Connector Arrow - Hidden on mobile */}
              <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                <ArrowRight size={24} className="text-[var(--gold)]" />
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[var(--truffle)] to-[var(--truffle-dark)] rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-white shadow-xl h-full">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-white text-[var(--truffle)] flex items-center justify-center font-cinzel font-bold text-xl shadow-lg border-2 border-[var(--truffle)]">2</div>
                <div className="pt-4">
                  <h3 className="text-xl sm:text-2xl font-cinzel font-semibold mb-3">Multi-System Analysis</h3>
                  <p className="text-sm sm:text-base text-white/80 mb-4 leading-relaxed">
                    We cross-validate using 6 classical Dasha systems — Vimshottari, Yogini, Chara, Ashtottari, and more.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/10 rounded-lg px-3 py-2 text-xs text-center">Astronomical Precision</div>
                    <div className="bg-white/10 rounded-lg px-3 py-2 text-xs text-center">Classical Validation</div>
                    <div className="bg-white/10 rounded-lg px-3 py-2 text-xs text-center">Transit Mapping</div>
                    <div className="bg-white/10 rounded-lg px-3 py-2 text-xs text-center">Yoga Detection</div>
                  </div>
                </div>
              </div>
              {/* Connector Arrow - Hidden on mobile */}
              <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                <ArrowRight size={24} className="text-[var(--gold)]" />
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-[var(--gold)] shadow-lg h-full">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-br from-[var(--gold)] to-amber-500 text-white flex items-center justify-center font-cinzel font-bold text-xl shadow-lg">3</div>
                <div className="pt-4">
                  <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-3">Human-Readable Clarity</h3>
                  <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-4 leading-relaxed">
                    AI translates the analysis into calm, clear guidance you can actually use. No charts. No jargon. No fear.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 text-xs bg-[var(--gold)]/10 text-amber-700 rounded-full font-medium">Action-Oriented</span>
                    <span className="px-3 py-1 text-xs bg-[var(--gold)]/10 text-amber-700 rounded-full font-medium">Timing-Aware</span>
                    <span className="px-3 py-1 text-xs bg-[var(--gold)]/10 text-amber-700 rounded-full font-medium">Always Calm</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Trust Guarantees */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[var(--palladian)]/10 to-[var(--palladian)]/20 rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-[var(--palladian)]/30"
          >
            <div className="text-center mb-8">
              <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-[var(--text-primary)] mb-2">Our Promise to You</h3>
              <p className="text-sm text-[var(--text-secondary)]">What we will NEVER do:</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-white border-2 border-[var(--palladian)] flex items-center justify-center mb-3 shadow-sm">
                  <X size={20} className="text-[var(--palladian-dark)]" weight="bold" />
                </div>
                <p className="text-sm font-medium text-[var(--text-primary)]">No Fear-Based Language</p>
                <p className="text-xs text-[var(--text-muted)]">We don't create anxiety</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-white border-2 border-[var(--palladian)] flex items-center justify-center mb-3 shadow-sm">
                  <X size={20} className="text-[var(--palladian-dark)]" weight="bold" />
                </div>
                <p className="text-sm font-medium text-[var(--text-primary)]">No Guaranteed Outcomes</p>
                <p className="text-xs text-[var(--text-muted)]">We guide, not guarantee</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-white border-2 border-[var(--palladian)] flex items-center justify-center mb-3 shadow-sm">
                  <X size={20} className="text-[var(--palladian-dark)]" weight="bold" />
                </div>
                <p className="text-sm font-medium text-[var(--text-primary)]">No Raw Astro Jargon</p>
                <p className="text-xs text-[var(--text-muted)]">Always in plain language</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-white border-2 border-[var(--palladian)] flex items-center justify-center mb-3 shadow-sm">
                  <X size={20} className="text-[var(--palladian-dark)]" weight="bold" />
                </div>
                <p className="text-sm font-medium text-[var(--text-primary)]">No Data Selling</p>
                <p className="text-xs text-[var(--text-muted)]">Your data stays yours</p>
              </div>
            </div>
          </motion.div>
          
          {/* Section CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              className="btn-primary text-lg px-10 py-5 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1"
              onClick={() => navigate("/auth")}
              data-testid="section3-cta-btn"
            >
              Experience the Difference
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Section 4: Real Questions, Real Clarity */}
      <section id="features" className="py-16 sm:py-28 bg-gradient-to-b from-white via-[var(--bg-paper)] to-[var(--bg-cream)] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--palladian)]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[var(--burning-flame)]/5 rounded-full blur-3xl" />
        
        <div className="container-app relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--palladian-light)] text-[var(--truffle)] text-sm font-manrope font-semibold mb-4 border border-[var(--palladian)]">
              <Brain size={14} weight="fill" />
              Real Questions, Real Clarity
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-cinzel mb-4 sm:mb-6 text-[var(--text-primary)] leading-tight">
              The Questions That<br className="hidden sm:block" /> <span className="text-[var(--truffle)]">Keep You Up at Night</span>
            </h2>
            <p className="text-lg sm:text-xl text-[var(--text-secondary)] max-w-3xl mx-auto leading-relaxed font-manrope">
              AADHYAA doesn't give you horoscopes. It gives you answers to the questions 
              that actually matter in your life — grounded in your unique rhythm.
            </p>
          </motion.div>

          {/* Question Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12">
            
            {/* Question 1: Career */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 card-glow-animated"
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--truffle)] to-[var(--truffle-dark)] flex items-center justify-center flex-shrink-0 shadow-lg card-icon-animated">
                  <Lightning size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--truffle)] uppercase tracking-wider mb-1 font-manrope">Career & Decisions</p>
                  <h3 className="text-lg sm:text-xl font-cinzel font-semibold text-[var(--text-primary)]">"Should I take this job offer?"</h3>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-[var(--bg-subtle)] rounded-xl p-4">
                  <p className="text-sm text-[var(--text-secondary)] italic font-manrope">"I have two offers. One pays more but feels off. The other excites me but pays less. What does my chart say?"</p>
                </div>
                <div className="bg-[var(--palladian-light)]/50 rounded-xl p-4 border-l-4 border-[var(--truffle)]">
                  <p className="text-xs font-semibold text-[var(--truffle)] mb-1 font-manrope">AADHYAA's Approach:</p>
                  <p className="text-sm text-[var(--text-primary)] font-manrope">We analyze your current Dasha period, career house strengths, and upcoming transits to show which path aligns with your natural timing. No guarantees — just informed clarity.</p>
                </div>
              </div>
            </motion.div>

            {/* Question 2: Relationships */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 card-glow-animated"
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--burning-flame)] to-[var(--burning-flame-dark)] flex items-center justify-center flex-shrink-0 shadow-lg card-icon-animated">
                  <Heart size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--burning-flame)] uppercase tracking-wider mb-1 font-manrope">Relationships</p>
                  <h3 className="text-lg sm:text-xl font-cinzel font-semibold text-[var(--text-primary)]">"Why does this relationship feel stuck?"</h3>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-[var(--bg-subtle)] rounded-xl p-4">
                  <p className="text-sm text-[var(--text-secondary)] italic font-manrope">"We love each other, but something's blocking us. Family pressure? Timing? Or is it just not meant to be?"</p>
                </div>
                <div className="bg-[var(--burning-flame)]/10 rounded-xl p-4 border-l-4 border-[var(--burning-flame)]">
                  <p className="text-xs font-semibold text-[var(--burning-flame)] mb-1 font-manrope">AADHYAA's Approach:</p>
                  <p className="text-sm text-[var(--text-primary)] font-manrope">We look at both charts' harmony, timing alignment, and family dynamics (if Kula tier). Not "you'll marry" — but "here's what's happening and what might help."</p>
                </div>
              </div>
            </motion.div>

            {/* Question 3: Children */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[var(--border-gold)] shadow-lg hover:shadow-2xl transition-all duration-500 card-glow-animated"
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--palladian)] to-[var(--palladian-dark)] flex items-center justify-center flex-shrink-0 shadow-lg card-icon-animated">
                  <Users size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--palladian-dark)] uppercase tracking-wider mb-1 font-manrope">Family & Children</p>
                  <h3 className="text-lg sm:text-xl font-cinzel font-semibold text-[var(--text-primary)]">"How do I support my child better?"</h3>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-[var(--bg-subtle)] rounded-xl p-4">
                  <p className="text-sm text-[var(--text-secondary)] italic font-manrope">"My daughter is struggling at school. Is she in the wrong stream? Should I push harder or give space?"</p>
                </div>
                <div className="bg-[var(--palladian)]/20 rounded-xl p-4 border-l-4 border-[var(--palladian)]">
                  <p className="text-xs font-semibold text-[var(--palladian-dark)] mb-1 font-manrope">AADHYAA's Approach:</p>
                  <p className="text-sm text-[var(--text-primary)] font-manrope">Your child's chart shows natural strengths and learning style. We help you understand their rhythm — not force a path. Plus: Kids Gurukul stories to build values together.</p>
                </div>
              </div>
            </motion.div>

            {/* Question 4: Timing */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-[var(--gold)] shadow-xl hover:shadow-2xl transition-all duration-500 card-glow-animated"
            >
              <div className="absolute -top-3 right-6 px-3 py-1 bg-gradient-to-r from-[var(--gold)] to-[var(--burning-flame)] text-white text-xs font-bold rounded-full shadow-md font-manrope">
                MOST ASKED
              </div>
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--gold)] to-[var(--burning-flame)] flex items-center justify-center flex-shrink-0 shadow-lg card-icon-animated">
                  <Calendar size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--gold)] uppercase tracking-wider mb-1 font-manrope">Timing</p>
                  <h3 className="text-lg sm:text-xl font-cinzel font-semibold text-[var(--text-primary)]">"When is the right time for this?"</h3>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-[var(--bg-subtle)] rounded-xl p-4">
                  <p className="text-sm text-[var(--text-secondary)] italic font-manrope">"I want to start my business / get married / buy property. Is this year good? This month? Should I wait?"</p>
                </div>
                <div className="bg-[var(--gold)]/10 rounded-xl p-4 border-l-4 border-[var(--gold)]">
                  <p className="text-xs font-semibold text-[var(--truffle)] mb-1 font-manrope">AADHYAA's Approach:</p>
                  <p className="text-sm text-[var(--text-primary)] font-manrope">The Timing & Decisions section shows your current phase, readiness scores, and optimal windows. Not "this is lucky" — but "this is when YOUR energy supports this kind of action."</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Quote Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center bg-gradient-to-r from-[var(--palladian-light)] via-white to-[var(--palladian-light)] rounded-2xl sm:rounded-3xl p-8 sm:p-12 border border-[var(--palladian)]"
          >
            <p className="text-xl sm:text-2xl lg:text-3xl font-cinzel text-[var(--text-primary)] leading-relaxed mb-4">
              "The goal isn't to predict your future.<br className="hidden sm:block" />
              It's to help you <span className="text-[var(--truffle)] font-semibold">meet it with clarity.</span>"
            </p>
            <p className="text-sm text-[var(--text-muted)] font-manrope">— The AADHYAA Philosophy</p>
          </motion.div>
          
          {/* Section CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              className="btn-primary text-lg px-10 py-5 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1"
              onClick={() => navigate("/auth")}
              data-testid="section4-cta-btn"
            >
              Ask Your First Question
              <ArrowRight size={20} className="ml-2" />
            </Button>
            <p className="text-sm text-[var(--text-muted)] mt-4">Free tier includes 1 question per day</p>
          </motion.div>
        </div>
      </section>
      
      {/* Section 5: Tier & Pricing Showcase (Premium, Calm, Decision-Oriented) */}
      <section id="pricing" className="py-16 sm:py-28 bg-gradient-to-b from-[var(--bg-paper)] via-white to-[var(--bg-cream)] relative overflow-hidden">
        {/* Subtle Background Elements */}
        <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-[var(--bg-paper)] to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-[var(--bg-cream)] to-transparent" />
        <div className="absolute top-1/4 right-0 w-80 h-80 bg-[var(--palladian)]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[var(--truffle)]/5 rounded-full blur-3xl" />
        
        <div className="container-app relative z-10">
          {/* 1. Section Header (Verbatim Copy) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-cinzel mb-4 sm:mb-6 text-[var(--text-primary)] leading-tight">
              Choose the depth that fits your life right now
            </h2>
            <p className="text-base sm:text-lg text-[var(--text-secondary)] max-w-3xl mx-auto leading-relaxed">
              All plans include access to the full AADHYAA experience. What changes is the depth, precision, and actionability.
            </p>
          </motion.div>
          
          {/* 2. Visual Progression Ladder */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center items-center gap-2 sm:gap-4 mb-8 overflow-x-auto px-4 py-2"
          >
            {PRICING_TIERS.map((tier, index) => (
              <div key={tier.id} className="flex items-center">
                <div 
                  className={`flex flex-col items-center px-3 sm:px-5 py-2 sm:py-3 rounded-xl transition-all ${
                    tier.isPopular 
                      ? 'bg-[var(--truffle)] text-white scale-105 shadow-lg' 
                      : 'bg-white/80 text-[var(--text-primary)] border border-[var(--border-muted)]'
                  }`}
                >
                  <span className="text-lg sm:text-xl mb-0.5">{tier.emoji}</span>
                  <span className={`text-xs sm:text-sm font-cinzel font-semibold ${tier.isPopular ? 'text-white' : 'text-[var(--text-primary)]'}`}>
                    {tier.name}
                  </span>
                  <span className={`text-[10px] sm:text-xs ${tier.isPopular ? 'text-white/80' : 'text-[var(--text-muted)]'}`}>
                    {tier.nameHindi} • {tier.nameEnglish}
                  </span>
                </div>
                {index < PRICING_TIERS.length - 1 && (
                  <ArrowRight size={16} className="text-[var(--text-muted)] mx-1 sm:mx-2 flex-shrink-0" />
                )}
              </div>
            ))}
          </motion.div>
          
          {/* Billing Cycle Toggle */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex justify-center items-center gap-4 mb-10 sm:mb-14"
          >
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-[var(--truffle)] text-white shadow-lg'
                  : 'bg-white text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--truffle)]'
              }`}
              data-testid="landing-billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all relative ${
                billingCycle === 'yearly'
                  ? 'bg-[var(--truffle)] text-white shadow-lg'
                  : 'bg-white text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--truffle)]'
              }`}
              data-testid="landing-billing-yearly"
            >
              Yearly
              <span className="absolute -top-2 -right-3 px-2 py-0.5 text-[10px] font-bold bg-[var(--palladian)] text-white rounded-full">
                Save 20%
              </span>
            </button>
          </motion.div>
          
          {/* 3. Enhanced Tier Cards - Modern 3-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-14 max-w-6xl mx-auto">
            {PRICING_TIERS.map((tier, index) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className={`relative group ${tier.isPopular ? 'lg:-mt-4 lg:mb-4' : ''}`}
              >
                {/* Glow Effect for Popular Tier */}
                {tier.isPopular && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[var(--truffle)]/20 via-[var(--palladian)]/10 to-[var(--burning-flame)]/20 rounded-3xl blur-xl transform group-hover:scale-105 transition-transform duration-500" />
                )}
                
                <div
                  className={`relative h-full flex flex-col rounded-3xl p-6 sm:p-8 transition-all duration-300 ${
                    tier.isPopular 
                      ? 'bg-gradient-to-br from-[var(--truffle)] via-[var(--truffle)] to-[#3a3428] text-white shadow-2xl border-2 border-[var(--gold)]/30' 
                      : tier.isFree
                        ? 'bg-white border-2 border-[var(--border-muted)] hover:border-[var(--palladian)] hover:shadow-xl'
                        : 'bg-gradient-to-br from-[var(--palladian)]/10 to-white border-2 border-[var(--palladian)]/30 hover:border-[var(--palladian)] hover:shadow-xl'
                  }`}
                  data-testid={`tier-card-${tier.id}`}
                >
                  {/* Popular Badge with Animation */}
                  {tier.isPopular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="relative">
                        <span className="px-5 py-1.5 text-xs font-bold bg-gradient-to-r from-[var(--gold)] to-amber-400 text-[var(--truffle)] rounded-full shadow-lg flex items-center gap-1.5">
                          <Sparkle size={12} weight="fill" className="animate-pulse" />
                          Most Chosen
                          <Sparkle size={12} weight="fill" className="animate-pulse" />
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Tier Header */}
                  <div className="text-center mb-6">
                    {/* Icon with Gradient Background */}
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${
                      tier.isPopular 
                        ? 'bg-white/10 backdrop-blur-sm border border-white/20' 
                        : tier.isFree 
                          ? 'bg-[var(--palladian)]/10 border border-[var(--palladian)]/20'
                          : 'bg-gradient-to-br from-[var(--palladian)]/20 to-[var(--palladian)]/5 border border-[var(--palladian)]/30'
                    }`}>
                      <span className="text-3xl">{tier.emoji}</span>
                    </div>
                    
                    {/* Tier Name */}
                    <h3 className={`text-2xl sm:text-3xl font-cinzel font-bold mb-1 ${tier.isPopular ? 'text-white' : 'text-[var(--text-primary)]'}`}>
                      {tier.name}
                    </h3>
                    <p className={`text-sm ${tier.isPopular ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>
                      {tier.nameHindi} • {tier.nameEnglish}
                    </p>
                  </div>
                  
                  {/* Tagline */}
                  <p className={`text-center text-sm sm:text-base font-medium italic mb-4 ${
                    tier.isPopular ? 'text-[var(--gold)]' : 'text-[var(--truffle)]'
                  }`}>
                    "{tier.tagline}"
                  </p>
                  
                  {/* Target User */}
                  <p className={`text-center text-sm mb-6 leading-relaxed ${
                    tier.isPopular ? 'text-white/80' : 'text-[var(--text-secondary)]'
                  }`}>
                    {tier.whoItsFor}
                  </p>
                  
                  {/* Pricing Block */}
                  <div className={`text-center py-5 px-4 rounded-2xl mb-6 ${
                    tier.isPopular 
                      ? 'bg-white/10 backdrop-blur-sm border border-white/10' 
                      : 'bg-[var(--bg-cream)]/50 border border-[var(--border-muted)]'
                  }`}>
                    {tier.isFree ? (
                      <div>
                        <span className={`text-4xl sm:text-5xl font-bold ${tier.isPopular ? 'text-white' : 'text-[var(--palladian-text)]'}`}>
                          Free
                        </span>
                        <p className={`text-sm mt-2 ${tier.isPopular ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>
                          Forever • No credit card
                        </p>
                      </div>
                    ) : billingCycle === 'monthly' ? (
                      <div>
                        <div className={`text-4xl sm:text-5xl font-bold ${tier.isPopular ? 'text-white' : 'text-[var(--truffle)]'}`}>
                          {isIndia ? `₹${tier.monthlyPriceINR}` : `$${tier.monthlyPriceUSD}`}
                          <span className={`text-lg font-normal ${tier.isPopular ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>/mo</span>
                        </div>
                        <p className={`text-sm mt-2 ${tier.isPopular ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>
                          Cancel anytime
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className={`text-4xl sm:text-5xl font-bold ${tier.isPopular ? 'text-white' : 'text-[var(--truffle)]'}`}>
                          {isIndia ? `₹${tier.yearlyPriceINR.toLocaleString()}` : `$${tier.yearlyPriceUSD}`}
                          <span className={`text-lg font-normal ${tier.isPopular ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>/yr</span>
                        </div>
                        <p className={`text-sm mt-2 font-semibold ${tier.isPopular ? 'text-[var(--gold)]' : 'text-[var(--palladian-text)]'}`}>
                          Save {Math.round((1 - tier.yearlyPriceINR / (tier.monthlyPriceINR * 12)) * 100)}% vs monthly
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Features List */}
                  <div className="flex-grow mb-6">
                    <p className={`text-[10px] uppercase tracking-widest font-bold mb-3 ${
                      tier.isPopular ? 'text-white/60' : 'text-[var(--text-muted)]'
                    }`}>
                      What you unlock
                    </p>
                    <ul className="space-y-3">
                      {tier.unlocks.map((unlock, i) => (
                        <li key={`${unlock}-${i}`} className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                            tier.isPopular 
                              ? 'bg-[var(--gold)]/20 border border-[var(--gold)]/40' 
                              : 'bg-[var(--palladian)]/15 border border-[var(--palladian)]/30'
                          }`}>
                            <Check size={12} weight="bold" className={tier.isPopular ? 'text-[var(--gold)]' : 'text-[var(--palladian-text)]'} />
                          </div>
                          <span className={`text-sm ${tier.isPopular ? 'text-white/90' : 'text-[var(--text-secondary)]'}`}>
                            {unlock}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* CTA Button */}
                  <Button
                    onClick={() => navigate("/auth", { state: { selectedTier: tier.id, billingCycle } })}
                    className={`w-full py-4 text-base font-semibold rounded-xl transition-all duration-300 ${
                      tier.isPopular
                        ? 'bg-gradient-to-r from-[var(--gold)] via-amber-400 to-[var(--gold)] text-[var(--truffle)] hover:shadow-lg hover:shadow-[var(--gold)]/30 hover:scale-[1.02]'
                        : tier.isFree
                          ? 'bg-white border-2 border-[var(--truffle)] text-[var(--truffle)] hover:bg-[var(--truffle)] hover:text-white'
                          : 'bg-[var(--truffle)] text-white hover:bg-[var(--truffle-dark)] hover:shadow-lg'
                    }`}
                    data-testid={`tier-cta-${tier.id}`}
                  >
                    {tier.cta}
                    <ArrowRight size={18} className="ml-2 inline" />
                  </Button>
                  
                  {/* Trust Badge for Popular */}
                  {tier.isPopular && (
                    <p className="text-center text-xs text-white/50 mt-4 flex items-center justify-center gap-1.5">
                      <Shield size={12} />
                      Chosen by 10,000+ seekers
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* 6. Reassurance Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-10 py-6 px-8 bg-[var(--palladian)]/10 rounded-2xl border border-[var(--palladian)]/30"
          >
            <p className="text-sm sm:text-base text-[var(--text-primary)] leading-relaxed font-medium">
              "All tiers include access to the same seven life sections. What changes is the depth of clarity — not the experience."
            </p>
          </motion.div>
          
          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button 
              className="btn-primary text-lg px-10 py-5 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1"
              onClick={() => navigate("/auth")}
              data-testid="section5-cta-btn"
            >
              Begin Your Journey
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Footer - Three Layer Structure */}
      <footer className="bg-[var(--abyssal)] text-[var(--bg-cream)]" data-testid="landing-footer">
        {/* Layer 3: Brand Statement */}
        <div className="py-6 border-b border-white/10">
          <div className="container-app text-center">
            <p className="text-sm sm:text-base text-[var(--palladian-text)] font-medium tracking-wide">
              AADHYAA — Rooted in Vedic Wisdom. Refined by Modern Science. Guided by Responsible AI.
            </p>
          </div>
        </div>
        
        {/* Layer 2: Made in India + Navigation */}
        <div className="py-4 border-b border-white/10">
          <div className="container-app">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs sm:text-sm text-[var(--oatmeal)] flex items-center gap-2">
                <span className="inline-block w-5 h-3.5 rounded-sm overflow-hidden flex-shrink-0 shadow-sm">
                  <span className="block h-[5px] bg-[#FF9933]"></span>
                  <span className="block h-[4px] bg-white"></span>
                  <span className="block h-[5px] bg-[#138808]"></span>
                </span>
                Proudly made in India for the world
              </p>
              <button 
                onClick={() => navigate("/legal")} 
                className="text-sm px-4 py-2 rounded-lg text-[var(--oatmeal)] hover:text-white hover:bg-[var(--blue-fantastic)] transition-colors flex items-center gap-2 min-h-[44px]"
                data-testid="trust-responsibility-btn"
              >
                <Shield size={16} />
                <span>Trust & Responsibility</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Layer 1: Copyright */}
        <div className="py-4">
          <div className="container-app">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <Logo variant="full" size="sm" testId="footer-logo" />
              <p className="text-xs text-[var(--oatmeal)]/80 text-center">
                © {new Date().getFullYear()} Aadhyaa Tasteful Traditions LLP. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
